import { useEffect, useState } from "react";
import { useCreatePortfolio, useEditPortfolio } from "../mutations";
import { validationRules } from "../../../shared/utils/validationRules";
import { useFetchPortfolioDetails } from "./useFetchPortfolioDetails";
import { useFetchPortfolios } from "./useFetchPortfolios";
import { formatApiEndDate, formatApiStartDate } from "../../../shared/utils";

export const useAddPortfolio = ({
  isEdit,
  portfolioId,
}: {
  isEdit: boolean;
  portfolioId: string | undefined;
}) => {

  const createPortfolio = useCreatePortfolio();
  const editPortfolio = useEditPortfolio();

  const { data, isError, error, isLoading } = useFetchPortfolioDetails({ portfolioId });
  const { removeCache } = useFetchPortfolios(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    message: string | null;
    programme: number | null;
    portfolio: number | null;
    portfolio_name: string | null;
  } | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<{
    programme: number | null;
    portfolio_name: string | null;
    description: string | null;
    start_date: string | null;
    end_date: string | null;
  }>({
    programme: null,
    portfolio_name: null,
    description: null,
    start_date: null,
    end_date: null,
  });

  const validationConfig: Record<string, string[]> = {
    programme: ["is_empty"],
    portfolio_name: ["is_empty"],
    description: ["is_empty"],
    start_date: ["is_empty"],
    end_date: ["is_empty"],
  };

  const validateField = (key: string, value: string): string | null => {
    const rules = validationConfig[key];
    if (!rules) return null;
    for (const ruleName of rules) {
      const rule = validationRules[ruleName];
      if (rule) {
        const error = rule(value);
        if (error) return error;
      }
    }
    return null;
  };

  const validateAllFields = (): { isValid: boolean, errors: any } => {
    let newErrors: Record<string, string | null> = {};

    Object.keys(validationConfig).forEach((key) => {
      const error = validateField(key, (formData as any)[key]);
      if (error) newErrors[key] = error;
    });
    setFieldErrors(newErrors);

    const isValid = Object.values(newErrors).every((error) => !error);

    return { isValid, errors: newErrors };
  };

  const handleFieldChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    const error = validateField(key, value);
    setFieldErrors((prev) => ({ ...prev, [key]: error }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const { isValid, errors } = validateAllFields();
    if (!isValid) {
      console.warn("Validation Failed:", errors);
      setIsSubmitting(false);
      return;
    }

    const payload = {
      programme_id: formData?.programme,
      portfolio_name: formData?.portfolio_name ?? "",
      description: formData?.description ?? "",
      start_date: formatApiStartDate(formData?.start_date),
      end_date: formatApiEndDate(formData?.end_date),
    };

    try {
      if (isEdit && portfolioId) {
        await editPortfolio.mutateAsync({ portfolioId, payload });
        setSubmitMessage({
          type: "success",
          message: "Portfolio updated successfully!",
          programme: null,
          portfolio: null,
          portfolio_name: null,
        });
      } else {
        const response = await createPortfolio.mutateAsync({ payload });
        setSubmitMessage({
          type: "success",
          message: "Portfolio created successfully!",
          programme: formData.programme,
          portfolio: response.data.id,
          portfolio_name: formData.portfolio_name,
        });
      }
      removeCache();
      setFormData({
        programme: null,
        portfolio_name: null,
        description: null,
        start_date: null,
        end_date: null,
      });
    } catch (error: any) {

      const errorCode = error?.response?.data?.data?.code;
      const errorMessage = error?.response?.data?.data?.message;

      if (errorCode === "PORTFOLIO_NAME_ALREADY_EXISTS") {

        setFieldErrors((prev) => ({
          ...prev,
          portfolio_name: errorMessage || "A portfolio with this name already exists. Please use a different portfolio name.",
        }));

      } else {

        setSubmitMessage({
          type: "error",
          message: error?.response?.data?.message || (isEdit ? "Failed to update portfolio. Please try again." : "Failed to create portfolio. Please try again."),
          programme: null,
          portfolio: null,
          portfolio_name: null,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isEdit && data) {
      setFormData({
        programme: data.programme_id,
        portfolio_name: data.portfolio_name,
        description: data.description,
        start_date: data.start_date ? new Date(data.start_date).toISOString().split("T")[0] : null,
        end_date: data.end_date ? new Date(data.end_date).toISOString().split("T")[0] : null,
      });
    }
  }, [data, isEdit]);

  return {
    formData,
    fieldErrors,
    setFormData,
    handleFieldChange,
    isSubmitting,
    handleSubmit,
    submitMessage,
    setSubmitMessage,
    isLoading,
    isError,
    error,
  };
};
